import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import DummyBadge from '../../common/DummyBadge';
import Sparkline from './Sparkline';
import { AnimatedNumber, useCardReveal } from './motion';
import { cardSx, heroSx, labelSx, valueSx, toneColor, toneSoft } from './styles';

const DEMO = 'Demo data — waits for the dashboard backend';

const deltaLabel = (d) => {
  if (!d) return null;
  if (d.direction === 'new') return 'new';
  if (d.direction === 'flat') return '— 0%';
  return `${d.direction === 'up' ? '▲' : '▼'} ${Math.abs(d.pct)}%`;
};

/**
 * Headline number: label, value (+unit), delta pill (up green / down red; `new` without a previous value),
 * a short fact, optional sparkline. `hero` = the teal gradient card. `goodWhenDown` flips the pill colour
 * (turnaround: faster is good).
 */
const HeadlineCard = ({
  label, value, unit, hero, icon, delta, deltaText, goodWhenDown, fact, spark, sparkColor, demo, tone, onClick, children,
}) => {
  const theme = useTheme();
  const reveal = useCardReveal();
  const text = deltaText || deltaLabel(delta);
  const dir = delta?.direction;
  const pillTone = dir === 'up' ? (goodWhenDown ? 'down' : 'up') : dir === 'down' ? (goodWhenDown ? 'up' : 'down') : 'grey';
  const pillSx = hero
    ? { bgcolor: 'rgba(255,255,255,.2)', color: '#fff' }
    : { bgcolor: toneSoft(theme, pillTone), color: toneColor(theme, pillTone) };
  return (
    <Box onClick={onClick} sx={{ ...cardSx, ...(hero ? heroSx : {}), cursor: onClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', ...reveal }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
        {icon}
      </Box>
      <Typography sx={{ ...labelSx, mt: 1.5, color: hero ? 'rgba(255,255,255,.75)' : 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {label}{demo && <DummyBadge label={DEMO} />}
      </Typography>
      <Typography sx={{ ...valueSx, mt: 0.5, color: tone ? toneColor(theme, tone) : 'inherit' }}>
        <AnimatedNumber value={value} />
        {unit && <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, ml: 0.75, opacity: 0.75 }}>{unit}</Box>}
      </Typography>
      {children}
      {(text || fact || spark) && (
      <Box data-testid="headline-foot" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 'auto', pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {text && (
            <Box component="span" data-tone={pillTone} sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.375, borderRadius: 999, whiteSpace: 'nowrap', ...pillSx }}>
              {text}
            </Box>
          )}
          {fact && <Typography sx={{ fontSize: '0.75rem', opacity: hero ? 0.85 : 1, color: hero ? 'inherit' : 'text.secondary' }} noWrap>{fact}</Typography>}
        </Box>
        {spark && <Sparkline values={spark} color={hero ? '#ffffff' : (sparkColor || theme.palette.primary.main)} />}
      </Box>
      )}
    </Box>
  );
};

export default HeadlineCard;
